import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { iconButtonSurfaceVariants, iconButtonVariants } from './icon-button-variants';

export interface IconButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      'aria-label' | 'aria-pressed'
    >,
    VariantProps<typeof iconButtonVariants> {
  children: React.ReactNode;
  isLoading?: boolean;
  isPressed?: boolean;
  label: string;
  ref?: React.Ref<HTMLButtonElement>;
}

function IconButton({
  children,
  className,
  disabled,
  isLoading = false,
  isPressed,
  label,
  onClick,
  'aria-busy': ariaBusy,
  ref,
  size,
  type,
  variant,
  ...props
}: IconButtonProps) {
  const handleClick: React.MouseEventHandler<HTMLButtonElement> | undefined = isLoading
    ? event => {
        event.preventDefault();
        event.stopPropagation();
      }
    : onClick;
  const isSelected = isPressed === true;

  return (
    <button
      {...props}
      ref={ref}
      type={type ?? 'button'}
      className={cn(iconButtonVariants({ variant, size, className }))}
      disabled={disabled || isLoading}
      aria-busy={isLoading ? true : ariaBusy}
      aria-label={label}
      aria-pressed={isPressed}
      onClick={handleClick}
    >
      <span
        aria-hidden="true"
        className={cn(
          iconButtonSurfaceVariants({ variant, size }),
          isSelected &&
            'bg-neutral-interaction-selected ring-1 ring-inset ring-neutral-interaction-selected-border'
        )}
        data-slot="icon-button-surface"
      >
        <span className={cn('inline-flex', isLoading && 'invisible')}>{children}</span>
      </span>
      {isLoading ? (
        <span
          aria-hidden="true"
          className="absolute size-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
          data-slot="icon-button-loading-indicator"
        />
      ) : null}
    </button>
  );
}

export { IconButton };
